import { toast } from '../ui/Toast.js';

class ApiService {
    constructor() {
        this.baseUrl = '/api';
        this.token = localStorage.getItem('jigsaw_token');
        this.user = JSON.parse(localStorage.getItem('jigsaw_user') || 'null');
    }

    isLoggedIn() {
        return !!this.token;
    }

    getCurrentUser() {
        return this.user;
    }

    async _request(endpoint, options = {}, showError = true) {
        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        const config = {
            ...options,
            headers
        };

        try {
            const response = await fetch(`${this.baseUrl}${endpoint}`, config);
            const res = await response.json();

            // 处理业务逻辑错误 (code !== 0)
            if (res.code !== 0) {
                // 如果是 Token 失效 (后端约定 code 为 10002)
                if (res.code === 10002 || res.message === 'Invalid token' || res.message === 'Token expired') {
                    console.warn('Session expired or invalid, clearing local storage');
                    this._clearSession();
                }
                throw new Error(res.message || `Error ${res.code}`);
            }

            // 业务成功：如果有消息则显示 Toast
            if (res.message) {
                toast.success(res.message);
            }

            // 返回业务数据部分
            return res.data;

        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            if (showError) {
                toast.error(error.message);
            }
            throw error;
        }
    }

    async login(account, password) {
        const data = await this._request('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ account, password })
        });
        // data 已经是 res.data，包含 { token, user }
        this._setSession(data.token, data.user);
        return data.user;
    }

    async register(username, account, password) {
        const data = await this._request('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ username, account, password })
        });
        this._setSession(data.token, data.user);
        return data.user;
    }

    async googleLogin(googleToken) {
        const data = await this._request('/auth/google', {
            method: 'POST',
            body: JSON.stringify({ token: googleToken })
        });
        this._setSession(data.token, data.user);
        return data.user;
    }

    /**
     * Initiate OAuth 2.0 Authorization Code redirect to Google's consent screen.
     */
    startGoogleOAuth() {
        const state = crypto.randomUUID();
        sessionStorage.setItem('google_oauth_state', state);

        const params = new URLSearchParams({
            client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
            redirect_uri: `${window.location.origin}/api/auth/google-callback`,
            response_type: 'code',
            scope: 'openid email profile',
            state: state,
            prompt: 'select_account',
        });

        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    }

    /**
     * Handle OAuth callback by parsing auth data from URL hash fragment.
     * Returns { user } on success, { error } on failure, or null if no callback present.
     */
    handleOAuthCallback() {
        const hash = window.location.hash;
        if (!hash) return null;

        // Handle error
        if (hash.startsWith('#auth_error=')) {
            const errorMessage = decodeURIComponent(hash.substring('#auth_error='.length));
            history.replaceState(null, '', window.location.pathname + window.location.search);
            return { error: errorMessage };
        }

        // Handle auth data
        if (!hash.startsWith('#auth=')) return null;

        try {
            const base64 = hash.substring('#auth='.length);
            const json = decodeURIComponent(escape(atob(base64)));
            const authData = JSON.parse(json);

            // Verify CSRF state
            const expectedState = sessionStorage.getItem('google_oauth_state');
            if (!expectedState || authData.state !== expectedState) {
                console.error('OAuth state mismatch');
                history.replaceState(null, '', window.location.pathname + window.location.search);
                return { error: 'Security verification failed. Please try again.' };
            }

            sessionStorage.removeItem('google_oauth_state');
            this._setSession(authData.token, authData.user);

            // Clean URL hash
            history.replaceState(null, '', window.location.pathname + window.location.search);
            return { user: authData.user };
        } catch (e) {
            console.error('Failed to parse OAuth callback data:', e);
            history.replaceState(null, '', window.location.pathname + window.location.search);
            return { error: 'Failed to process login response.' };
        }
    }

    logout() {
        this._clearSession();
        window.location.reload(); 
    }

    _clearSession() {
        this.token = null;
        this.user = null;
        localStorage.removeItem('jigsaw_token');
        localStorage.removeItem('jigsaw_user');
    }

    _setSession(token, user) {
        this.token = token;
        this.user = user;
        localStorage.setItem('jigsaw_token', token);
        localStorage.setItem('jigsaw_user', JSON.stringify(user));
    }

    async submitScore(puzzleId, timeMs) {
        if (!this.isLoggedIn()) return;
        return await this._request('/score/submit', {
            method: 'POST',
            body: JSON.stringify({ puzzleId, timeMs })
        });
    }

    async getLeaderboard(puzzleId) {
        return await this._request(`/leaderboard/${puzzleId}`, {
            method: 'GET'
        });
    }

    async updateProfile(username, avatar) {
        if (!this.isLoggedIn()) return;
        const data = await this._request('/user/update-profile', {
            method: 'POST',
            body: JSON.stringify({ username, avatar })
        });
        if (data && data.user) {
            this._setSession(this.token, data.user);
        }
        return data.user;
    }

    async changePassword(oldPassword, newPassword) {
        if (!this.isLoggedIn()) return;
        return await this._request('/user/change-password', {
            method: 'POST',
            body: JSON.stringify({ oldPassword, newPassword })
        });
    }

    async getCompletedPuzzles() {
        if (!this.isLoggedIn()) return [];
        return await this._request('/user/completed-puzzles', {
            method: 'GET'
        }, false); // Set showError to false to handle empty states silently
    }

    async getPuzzleLikes(puzzleId) {
        return await this._request(`/puzzle/likes/${puzzleId}`, {
            method: 'GET'
        }, false);
    }

    async toggleLike(puzzleId) {
        if (!this.isLoggedIn()) {
            toast.info('Please login to like this puzzle');
            return null;
        }
        return await this._request('/puzzle/like', {
            method: 'POST',
            body: JSON.stringify({ puzzleId })
        });
    }
}

export const apiService = new ApiService();